'use client'

import { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { Box, Text, Title } from '@mantine/core'
import { CardSlider } from '@/components/Refresh/CardSlider/CardSlider'
import { CardSliderCard } from '@/components/Refresh/CardSlider/CardSliderCard'
import { FullBleedWrapper } from '@/components/Refresh/CategoryPages/FullBleedWrapper'
import { refreshTitleVariantProps } from '@/components/Refresh/RefreshTypography'
import trustpilotStars from '@/assets/images/refresh/trustpilot/trustpilot-stars.png'
import { TrustpilotLogoBadge } from './TrustpilotLogoBadge'
import classes from './TrustpilotReviews.module.css'

export interface TrustpilotReview {
    title: string
    description: string
    name: string
}

export interface TrustpilotReviewsProps {
    sectionTitle: string
    reviews: TrustpilotReview[]
}

const CARD_GAP = 24
const CARD_RADIUS = 16
const CARD_STARS_WIDTH = 135
const CARD_STARS_HEIGHT = 26

/** Trustpilot review cards slider with section header badge (Figma Store 2.0 929-3853). */
export function TrustpilotReviews({ sectionTitle, reviews }: TrustpilotReviewsProps) {
    const titleRef = useRef<HTMLHeadingElement>(null)
    const [cardStartX, setCardStartX] = useState(0)

    useEffect(() => {
        const titleElement = titleRef.current
        if (!titleElement) return

        const updateStartX = () => {
            setCardStartX(Math.max(0, titleElement.getBoundingClientRect().left))
        }

        updateStartX()
        window.addEventListener('resize', updateStartX)
        const observer = new ResizeObserver(updateStartX)
        observer.observe(titleElement)

        return () => {
            window.removeEventListener('resize', updateStartX)
            observer.disconnect()
        }
    }, [])

    if (!reviews.length) return null

    return (
        <Box id="testimonialReviewsV2" className={classes.section}>
            <Box className={classes.header}>
                <Title ref={titleRef} className={classes.sectionTitle} {...refreshTitleVariantProps.section}>
                    {sectionTitle}
                </Title>
                <Box className={classes.headerBadge}>
                    <TrustpilotLogoBadge />
                </Box>
            </Box>

            <Box className={classes.sliderContainer}>
                <FullBleedWrapper>
                    <CardSlider
                        gap={CARD_GAP}
                        startX={cardStartX}
                        endGap={96}
                        useTranslateForStartX
                        initialPosition="end"
                    >
                        {reviews.map((review) => (
                            <CardSliderCard key={`${review.name}:${review.title}`} radius={CARD_RADIUS}>
                                <Box className={classes.reviewCard}>
                                    <Box className={classes.cardContent}>
                                        <Text className={classes.cardTitle}>{review.title}</Text>
                                        <Text className={classes.cardDescription}>{review.description}</Text>
                                    </Box>
                                    <Box className={classes.cardFooter}>
                                        <Text className={classes.cardName}>{review.name}</Text>
                                        <Image
                                            src={trustpilotStars}
                                            alt="5 out of 5 stars on Trustpilot"
                                            width={CARD_STARS_WIDTH * 2}
                                            height={CARD_STARS_HEIGHT * 2}
                                            style={{
                                                width: CARD_STARS_WIDTH,
                                                height: CARD_STARS_HEIGHT,
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </CardSliderCard>
                        ))}
                    </CardSlider>
                </FullBleedWrapper>
            </Box>
        </Box>
    )
}
